import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/eventTracking';
import {
  addDays,
  calcularTurnoDelDia,
  obtenerProximoTurno,
  toDateKey,
} from '../utils/calendario';
import { esCapaEjercicio } from '../lib/perfil';

// Tick para refrescar `now` y que las condiciones temporales del motor
// (horas hasta próximo turno, cooldown de dismiss) se re-evalúen sin
// que el usuario tenga que recargar la página.
const NOW_TICK_MS = 60_000;

/**
 * useOnboarding — orquesta el estado del onboarding/tour para alimentar
 * al motor de nudges. Combina:
 *   - user_onboarding (Supabase) — flags persistidos
 *   - profile.perfil_completado — desde profiles
 *   - rotacion activa + overrides — desde rotaciones, turno_overrides
 *   - próximo/último turno — calculado con utils/calendario
 *   - bitácora — count + entrada del día del último turno
 *
 * Expone `state` listo para pasar a getActiveNudges, getActiveTourStep,
 * getViajeFases del motor (src/lib/nudges.js), y mutadores para los
 * cambios de estado del tour.
 */
export function useOnboarding(userId, profile) {
  const [onboarding, setOnboarding] = useState(null);
  const [rotacion, setRotacion] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [primeraBitacoraEscrita, setPrimeraBitacoraEscrita] = useState(false);
  const [ultimoTurnoCerrado, setUltimoTurnoCerrado] = useState(true);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());

  const load = useCallback(async (signal) => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    // Idempotente. Para los 29 usuarios existentes el backfill ya creó la
    // fila; para signups futuros, esta es la inserción.
    await supabase.rpc('ensure_user_onboarding', { uid: userId });

    const [obRes, rotRes, ovRes, bitRes] = await Promise.all([
      supabase
        .from('user_onboarding')
        .select('tour_completado, tour_saltado, primer_turno_preparado_at, dismissed_nudges, tour_force_paso')
        .eq('user_id', userId)
        .maybeSingle(),
      supabase
        .from('rotaciones')
        .select('*')
        .eq('user_id', userId)
        .eq('activa', true)
        .maybeSingle(),
      supabase
        .from('turno_overrides')
        .select('fecha, tipo')
        .eq('user_id', userId),
      supabase
        .from('bitacora_entradas')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId),
    ]);

    if (signal?.aborted) return;

    setOnboarding(obRes.data || {
      tour_completado: false,
      tour_saltado: false,
      primer_turno_preparado_at: null,
      dismissed_nudges: {},
      tour_force_paso: null,
    });
    setRotacion(rotRes.data || null);
    setOverrides(ovRes.data || []);
    setPrimeraBitacoraEscrita((bitRes.count || 0) > 0);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  // Refresh manual: el consumer lo llama cuando el usuario hace una acción
  // externa que cambia los datos del backend (crear rotacion en
  // calendario-config, escribir bitácora, etc).
  const refresh = useCallback(() => load(), [load]);

  // Tick periódico para mantener fresco el `now` que ve el motor.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), NOW_TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Último turno: iterar hacia atrás desde hoy. Si el de hoy aún no terminó,
  // no cuenta como "último".
  const ultimoTurnoAt = useMemo(
    () => calcularUltimoTurno(now, rotacion, overrides),
    [now, rotacion, overrides],
  );

  // ¿Hay bitácora con fecha == fecha del último turno?
  useEffect(() => {
    if (!userId || !ultimoTurnoAt) { setUltimoTurnoCerrado(true); return; }
    const fechaKey = toDateKey(ultimoTurnoAt);
    let alive = true;
    supabase
      .from('bitacora_entradas')
      .select('id')
      .eq('user_id', userId)
      .eq('fecha', fechaKey)
      .maybeSingle()
      .then(({ data }) => { if (alive) setUltimoTurnoCerrado(!!data); });
    return () => { alive = false; };
  }, [userId, ultimoTurnoAt]);

  // Próximo turno: 1..30 días hacia adelante.
  const proximoTurnoAt = useMemo(() => {
    if (!rotacion) return null;
    const proximo = obtenerProximoTurno(now, rotacion, overrides);
    if (!proximo) return null;
    const [h, m] = (proximo.turno.horaInicio || '08:00').split(':').map(Number);
    const d = new Date(proximo.fecha);
    d.setHours(h, m, 0, 0);
    return d.toISOString();
  }, [now, rotacion, overrides]);

  // State compuesto para el motor.
  const state = useMemo(() => ({
    tour_completado: !!onboarding?.tour_completado,
    tour_saltado: !!onboarding?.tour_saltado,
    perfil_completo: !!profile?.perfil_completado,
    rotativa_cargada: !!rotacion,
    primer_turno_preparado: !!onboarding?.primer_turno_preparado_at,
    proximo_turno_at: proximoTurnoAt,
    ultimo_turno_at: ultimoTurnoAt ? ultimoTurnoAt.toISOString() : null,
    ultimo_turno_cerrado: ultimoTurnoCerrado,
    primera_bitacora_escrita: primeraBitacoraEscrita,
    es_capa_ejercicio: esCapaEjercicio(profile), // pro: no se le empuja la Bitácora
    dismissed_nudges: onboarding?.dismissed_nudges || {},
    tour_force_paso: onboarding?.tour_force_paso ?? null,
    rotacion,
    now,
  }), [
    onboarding, profile, rotacion,
    proximoTurnoAt, ultimoTurnoAt, ultimoTurnoCerrado, primeraBitacoraEscrita,
    now,
  ]);

  // ── Mutadores ───────────────────────────────────────────────────────────────

  const updateRow = useCallback(async (patch) => {
    if (!userId) return null;
    const { data, error } = await supabase
      .from('user_onboarding')
      .update(patch)
      .eq('user_id', userId)
      .select('tour_completado, tour_saltado, primer_turno_preparado_at, dismissed_nudges, tour_force_paso')
      .maybeSingle();
    if (error) return null;
    if (data) setOnboarding(data);
    return data;
  }, [userId]);

  const completeTour = useCallback(async () => {
    const data = await updateRow({ tour_completado: true });
    if (data) trackEvent('tour_completado', { modulo: 'onboarding' });
  }, [updateRow]);

  const skipTour = useCallback(async ({ enPaso } = {}) => {
    const data = await updateRow({ tour_saltado: true });
    if (data) trackEvent('tour_saltado', { modulo: 'onboarding', payload: { en_paso: enPaso ?? null } });
  }, [updateRow]);

  const dismissNudge = useCallback(async (nudgeId) => {
    const current = onboarding?.dismissed_nudges || {};
    const next = { ...current, [nudgeId]: new Date().toISOString() };
    const data = await updateRow({ dismissed_nudges: next });
    if (data) trackEvent('nudge_descartado', { modulo: 'home', payload: { nudge_id: nudgeId } });
  }, [updateRow, onboarding]);

  const markPrimerTurnoPreparado = useCallback(async () => {
    if (onboarding?.primer_turno_preparado_at) return; // idempotent
    await updateRow({ primer_turno_preparado_at: new Date().toISOString() });
  }, [updateRow, onboarding]);

  // Solo para QA. Llamado desde el botón "Reset tour" en /perfil (admin only).
  // Setea tour_force_paso = 1 para que el motor muestre el walkthrough
  // desde paso 1 ignorando que perfil_completado / rotativa siguen true.
  // El usuario avanza con advanceTourStep (CTA "Siguiente" en FirstJourney).
  const resetTour = useCallback(async () => {
    await updateRow({
      tour_completado: false,
      tour_saltado: false,
      primer_turno_preparado_at: null,
      tour_force_paso: 1,
    });
  }, [updateRow]);

  // Avanza el walkthrough en modo forzado (post-reset). Incrementa 1→2→3;
  // al pasar de 3, marca tour_completado y limpia tour_force_paso.
  // No-op si no estamos en modo forzado — en flujo normal el avance es
  // implícito vía cambio de estado real (perfil_completado, etc).
  const advanceTourStep = useCallback(async () => {
    const current = onboarding?.tour_force_paso;
    if (current == null) return;
    if (current >= 3) {
      const data = await updateRow({ tour_force_paso: null, tour_completado: true });
      if (data) trackEvent('tour_completado', { modulo: 'onboarding', payload: { via: 'walkthrough_forzado' } });
      return;
    }
    const next = current + 1;
    const data = await updateRow({ tour_force_paso: next });
    if (data) trackEvent('tour_paso_completado', { modulo: 'onboarding', payload: { paso: current, via: 'walkthrough_forzado' } });
  }, [updateRow, onboarding]);

  return {
    state,
    loading,
    refresh,
    completeTour,
    skipTour,
    dismissNudge,
    markPrimerTurnoPreparado,
    resetTour,
    advanceTourStep,
  };
}

// ── Helper local: análogo a obtenerProximoTurno pero hacia atrás ─────────────

function calcularUltimoTurno(now, rotacion, overrides) {
  if (!rotacion) return null;
  const hoy = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  for (let i = 0; i <= 30; i++) {
    const dia = addDays(hoy, -i);
    const override = Array.isArray(overrides)
      ? overrides.find(o => o.fecha === toDateKey(dia))
      : null;
    const turno = calcularTurnoDelDia(dia, rotacion, override);
    if (turno.tipo === 'libre') continue;
    if (i === 0) {
      // El de hoy solo cuenta como "último" si ya terminó.
      if (turno.terminaSiguienteDia) continue; // noche que aún no termina
      if (!turno.horaFin) continue;
      const [hF, mF] = turno.horaFin.split(':').map(Number);
      const finMin = hF * 60 + mF;
      const nowMin = now.getHours() * 60 + now.getMinutes();
      if (nowMin <= finMin) continue;
    }
    return dia;
  }
  return null;
}
